import React, { useRef, useState } from "react";
import { Quote, Star, Play, Pause } from "lucide-react";

const ROOFTOP_IMG = "https://customer-assets.emergentagent.com/job_create-snap/artifacts/rn08omlj_asset-b70a943c-a581-4dfd-b8da-8a310f5cfbd1.jpg";
const MAYA_IMG = "https://customer-assets.emergentagent.com/job_create-snap/artifacts/ebtvju8q_24242442.PNG";
const VIDEO_SRC_1 = "https://customer-assets.emergentagent.com/job_create-snap/artifacts/alpzjp5a_asset-1955b309-2a43-4201-9e6e-94853fed8df5.mov";

const VideoCard = ({ src, quote, name, role, badge = "VIDEO REVIEW", testId }) => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  return (
    <div className="snap-card overflow-hidden p-0" data-testid={testId}>
      <div className="relative bg-black aspect-[4/5]">
        <video
          ref={videoRef}
          src={src}
          className="w-full h-full object-cover"
          playsInline
          onEnded={() => setPlaying(false)}
          preload="metadata"
        />
        <button
          type="button"
          onClick={toggle}
          aria-label={playing ? "Pause testimonial" : "Play testimonial"}
          data-testid={`${testId}-toggle`}
          className="absolute inset-0 flex items-center justify-center group"
        >
          {!playing && (
            <span className="w-16 h-16 rounded-full bg-[#3B9EFF] text-white flex items-center justify-center shadow-[0_0_30px_rgba(59,158,255,0.6)] group-hover:scale-110 transition-transform">
              <Play size={26} fill="currentColor" />
            </span>
          )}
          {playing && (
            <span className="opacity-0 group-hover:opacity-100 w-14 h-14 rounded-full bg-black/60 text-white flex items-center justify-center transition-opacity">
              <Pause size={22} />
            </span>
          )}
        </button>
        <span className="absolute top-3 left-3 pill text-[10px]">{badge}</span>
      </div>
      <div className="p-6">
        <div className="flex items-center gap-1 text-[#3B9EFF] mb-3">
          {Array.from({ length: 5 }).map((_, k) => (
            <Star key={k} size={14} fill="currentColor" />
          ))}
        </div>
        <p className="text-white text-base leading-relaxed">"{quote}"</p>
        <div className="mt-4 pt-4 border-t border-[#3B9EFF]/10">
          <p className="font-display font-bold text-white">{name}</p>
          <p className="text-[#A0AABF] text-sm">{role}</p>
        </div>
      </div>
    </div>
  );
};

const ImageCard = ({ photo, name, role, quote, badge, testId, objectPosition = "center" }) => (
  <div className="snap-card overflow-hidden p-0" data-testid={testId}>
    <div className="relative aspect-[4/5] bg-[#0B111D]">
      <img
        src={photo}
        alt={`${name} — ${role}`}
        className="w-full h-full object-cover"
        style={{ objectPosition }}
        loading="lazy"
      />
      {badge && <span className="absolute top-3 left-3 pill text-[10px]">{badge}</span>}
      <div className="absolute bottom-0 inset-x-0 h-1/3 bg-gradient-to-t from-[#080C14] to-transparent" />
    </div>
    <div className="p-6">
      <Quote className="text-[#3B9EFF]" size={24} />
      <p className="mt-3 text-white text-base leading-relaxed">"{quote}"</p>
      <div className="mt-4 flex items-center gap-1 text-[#3B9EFF]">
        {Array.from({ length: 5 }).map((_, k) => (
          <Star key={k} size={14} fill="currentColor" />
        ))}
      </div>
      <div className="mt-4 pt-4 border-t border-[#3B9EFF]/10">
        <p className="font-display font-bold text-white">{name}</p>
        <p className="text-[#A0AABF] text-sm">{role}</p>
      </div>
    </div>
  </div>
);

export const Testimonials = () => {
  return (
    <section className="py-20 md:py-28" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="text-center">
          <span className="pill" data-testid="testimonials-pill">Real Customers</span>
          <h2 className="mt-5 font-display font-bold text-white text-4xl sm:text-5xl tracking-tighter">
            Business owners <span className="text-[#3B9EFF]">posting more.</span> <br className="hidden md:block" />
            Booking more.
          </h2>
        </div>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <ImageCard
            testId="testimonial-claudio"
            photo={ROOFTOP_IMG}
            name="Claudio"
            role="Owner, Rooftop Solutions — Florida"
            quote="My feed used to be dead. Now we post 3x a week — beautiful shots, clean branding, ready to go. I closed 4 new jobs from Instagram last month."
            badge="ROOFING"
            objectPosition="center top"
          />
          <VideoCard
            testId="testimonial-claudio-video"
            src={VIDEO_SRC_1}
            quote="So impressed with the video quality and product."
            name="Claudio"
            role="Owner, Rooftop Solutions"
          />
          <ImageCard
            testId="testimonial-maya"
            photo={MAYA_IMG}
            name="Maya L."
            role="Wellness Coach & Boutique Owner"
            quote="I have zero time for content. ContentSnap turned that around in 72 hours. The photos and reels look like I hired a full agency — but for a fraction."
            badge="WELLNESS"
            objectPosition="center 20%"
          />
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
